/**
 * Product UI locales (ADR-076).
 *
 * Repository language stays English. Display locales are British English and
 * Argentine Spanish so dates read the Argentine way (`03/04` = 3 April).
 *
 * Both are offered in the UI (admin header and marketing). The console still
 * starts in `es-AR`. Public marketing (`/` and `/equipos`) starts in `en-GB`
 * until the visitor stores a choice.
 */

export const APP_LOCALES = ["en-GB", "es-AR"] as const;

export type AppLocale = (typeof APP_LOCALES)[number];

/** Locales the language control may offer. */
export const SELECTABLE_LOCALES = ["es-AR", "en-GB"] as const satisfies readonly AppLocale[];

export const DEFAULT_LOCALE: AppLocale = "es-AR";

/** First visit to the public sales pages, when nothing is stored yet. */
export const MARKETING_DEFAULT_LOCALE: AppLocale = "en-GB";

export const LOCALE_STORAGE_KEY = "iot-admin.locale";

const MARKETING_PATHS = new Set(["/", "/equipos"]);

export const LOCALE_LABELS: Record<AppLocale, string> = {
  "en-GB": "English",
  "es-AR": "Español",
};

export function isAppLocale(value: string): value is AppLocale {
  return (APP_LOCALES as readonly string[]).includes(value);
}

export function isSelectableLocale(value: string): value is AppLocale {
  return (SELECTABLE_LOCALES as readonly string[]).includes(value);
}

export function isMarketingPath(pathname: string): boolean {
  return MARKETING_PATHS.has(pathname);
}

/** A choice the visitor made. `null` when nothing selectable is stored. */
export function readStoredLocaleChoice(): AppLocale | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);

    if (stored && isSelectableLocale(stored)) {
      return stored;
    }

    return null;
  } catch {
    return null;
  }
}

export function readStoredLocale(): AppLocale {
  return readStoredLocaleChoice() ?? DEFAULT_LOCALE;
}

/**
 * Stored choice wins. Otherwise the sales pages open in English and the
 * console stays in Spanish.
 */
export function localeForPath(pathname: string, stored: AppLocale | null): AppLocale {
  if (stored) {
    return stored;
  }

  return isMarketingPath(pathname) ? MARKETING_DEFAULT_LOCALE : DEFAULT_LOCALE;
}

export function writeStoredLocale(locale: AppLocale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Private mode or quota: the session still switches; only persistence fails.
  }
}
